import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import FormatLoyaltyBonusPoints from 'loyalty/components/Formatters/FormatLoyaltyBonusPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS } from 'loyalty/constants/loyaltyBonusPoints'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import React, { useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import * as Analytics from 'analytics/analytics'
import BodyText from 'LuxKit/Typography/BodyText'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'

/**
 * @NOTE LuxKit Gods I am sorry for I have sinned. I have created this in the absence of the MessageBanner component
 * having an option to have a vertical direction for its actions. I will refactor / delete this if that gets added or at
 * the end of the LuxPlus+ month campaign takeover.
 *
 * It's a duplicate of the LuxPlusRenewalMessageBanner component.
 */
function LuxPlusRenewalWarningSheet() {
  const intl = useIntl()
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const subscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const renewalStatus = subscriptionItem?.renewalStatus
  const dispatch = useAppDispatch()

  const handleRenewal = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'manual_renewal_purchase',
      action: 'add_to_cart_lux_plus_standalone',
      category: LUXURY_PLUS.ANALYTICS_CATEGORY,
      type: 'interaction',
    })
    dispatch(purchaseLuxPlus({ hasJoinFee: false }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (
    !subscriptionItem ||
    !isPendingRenewalLuxPlusMember ||
    renewalStatus !== 'ALLOW_RENEWAL' ||
    subscriptionItem.nextChargeAttemptDate === undefined
  ) return null

  const daysUntilNextChargeAttempt = daysUntilDate(new Date(subscriptionItem.nextChargeAttemptDate))

  const loyaltyBonus = <b>
    <FormattedMessage
      id="ovGJymSHfI"
      defaultMessage="{statusCredits} & {bonusPoints}"
      description="Status credits and bonus points renewal incentive in LuxPlus renewal warning sheet"
      values={{
        statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
        bonusPoints: <FormatLoyaltyBonusPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
      }}
    />
  </b>
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const programName = LUXURY_PLUS.PROGRAM_NAME

  return <Sheet
    background="critical-light"
    padding={16}
    className={getThemeClassName({ mobile: 'default' })}
  >
    <VerticalSpacer gap={16}>
      <VerticalSpacer gap={4}>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'q0y6ti1qLJ',
            defaultMessage: 'Your payment was declined',
            description: 'Heading shown when LuxPlus+ renewal payment fails',
          })}
        </BodyText>
        <BodyText variant="medium">
          {daysUntilNextChargeAttempt === 0 && isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
            id="q6VGOVdrp0"
            defaultMessage="Your benefits are on hold. We’ll try to process the payment again on {retryDate}. Renew now to receive your {loyaltyBonus} with {loyaltyProgramName} and continue enjoying {programName} pricing, bonus inclusions, early access & more."
            description="Payment retry on date with loyalty bonus renewal prompt for LuxPlus+ members"
            values={{
              retryDate: <FormatDateTime value={subscriptionItem.nextChargeAttemptDate} format="dmy-casual" />,
              loyaltyBonus,
              loyaltyProgramName,
              programName,
            }}
          />}
          {daysUntilNextChargeAttempt === 0 && !isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
            id="4QVmXfsjeG"
            defaultMessage="Your benefits are on hold. We’ll try to process the payment again on {retryDate}. Renew now to continue enjoying {programName} pricing, bonus inclusions, early access & more."
            description="Payment retry on date with renewal prompt for LuxPlus+ members"
            values={{
              retryDate: <FormatDateTime value={subscriptionItem.nextChargeAttemptDate} format="dmy-casual" />,
              programName,
            }}
          />}
          {daysUntilNextChargeAttempt !== 0 && isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
            id="ckhhdyp0KE"
            defaultMessage="Your benefits are on hold. We’ll try to process the payment again in {count, plural, one {# day} other {# days}}. Renew now to receive your {loyaltyBonus} with {loyaltyProgramName} and continue enjoying {programName} pricing, bonus inclusions, early access & more."
            description="Payment retry in days with loyalty bonus renewal prompt for LuxPlus+ members"
            values={{
              count: daysUntilNextChargeAttempt,
              loyaltyBonus,
              loyaltyProgramName,
              programName,
            }}
          />}
          {daysUntilNextChargeAttempt !== 0 && !isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
            id="HlO/bB4Fac"
            defaultMessage="Your benefits are on hold. We’ll try to process the payment again in {count, plural, one {# day} other {# days}}. Renew now to continue enjoying {programName} pricing, bonus inclusions, early access & more."
            description="Payment retry in days with renewal prompt for LuxPlus+ members"
            values={{
              count: daysUntilNextChargeAttempt,
              programName,
            }}
          />}
        </BodyText>
      </VerticalSpacer>
      <Group direction="horizontal" gap={8}>
        <TextButton kind="tertiary" to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}>
          {intl.formatMessage({
            id: '0DERdoHLHy',
            defaultMessage: 'Manage membership',
            description: 'Button to navigate to LuxPlus+ membership management page',
          })}
        </TextButton>
        <TextButton kind="secondary" onClick={handleRenewal}>
          {intl.formatMessage({
            id: 'Rwq0/XEsrJ',
            defaultMessage: 'Renew now',
            description: 'Button to immediately renew LuxPlus+ membership',
          })}
        </TextButton>
      </Group>
    </VerticalSpacer>
  </Sheet>
}

export default LuxPlusRenewalWarningSheet
